import { WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { useOrientationBasketSizeExperimentLimits } from "@src/appV2/ClipboardOrientation/hooks/useOrientationBasketSizeExperimentLimits";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import {
  type LimitedBasketSizeConfiguration,
  type WarningBasketSizeConfiguration,
} from "@src/appV2/FeatureFlags/CbhFeatureFlags";
import { LicenseStatuses } from "@src/appV2/Licenses/api/types";
import { type License, useGetLicenses } from "@src/appV2/Licenses/api/useGetLicenses";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { uniq } from "lodash";

function getUserQualifications(stage: WorkerStage, licenses: License[] | undefined): string[] {
  const statuses =
    stage === WorkerStage.ONBOARDING
      ? [LicenseStatuses.ACTIVE, LicenseStatuses.PENDING]
      : [LicenseStatuses.ACTIVE];

  return (
    licenses
      ?.filter((license) => statuses.includes(license.status))
      .map((license) => license.qualification) ?? []
  );
}

function classifyUserQualifications(params: {
  stage: WorkerStage;
  substageName: string;
  licenses: License[] | undefined;
  basketSizeLimitConfiguration: {
    limited: LimitedBasketSizeConfiguration;
    warning: WarningBasketSizeConfiguration;
  };
}): {
  targeted: string[];
  nonTargeted: string[];
} {
  const { stage, licenses, basketSizeLimitConfiguration, substageName } = params;
  let enabledQualifications: string[] = [];
  if (basketSizeLimitConfiguration.limited.enabled && substageName === "LIMITED") {
    enabledQualifications = basketSizeLimitConfiguration.limited.shiftRequirements;
  }

  if (basketSizeLimitConfiguration.warning.enabled && substageName === "WARNING") {
    enabledQualifications = basketSizeLimitConfiguration.warning.shiftRequirements;
  }

  const enabledQualificationsSet = new Set(enabledQualifications);
  const userQualifications = getUserQualifications(stage, licenses);

  return {
    targeted: uniq(
      userQualifications.filter((qualification) => enabledQualificationsSet.has(qualification))
    ),
    nonTargeted: uniq(
      userQualifications.filter((qualification) => !enabledQualificationsSet.has(qualification))
    ),
  };
}

export interface FetchSubstageResponse {
  status: "success" | "loading" | "error";
  data?:
    | {
        name: string;
        accessMetadata?: {
          scoreThreshold?: number;
          basketSize?: number;
          leadTimeThresholdInHrs?: number;
        };
        qualifications: string[];
        nonTargetedQualifications: string[];
      }
    | undefined;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function useFetchSubstage(): FetchSubstageResponse {
  const worker = useDefinedWorker();

  const orientationBasketSizeData = useOrientationBasketSizeExperimentLimits();
  const basketSizeLimitConfiguration = useCbhFlag(CbhFeatureFlag.BASKET_SIZE_LIMIT_CONFIGURATION, {
    defaultValue: {
      limited: {
        enabled: false,
      },
      warning: {
        enabled: false,
      },
    },
  });
  const { data: licenses, status: licensesStatus } = useGetLicenses(
    {
      workerId: worker.userId,
    },
    {
      enabled:
        basketSizeLimitConfiguration?.limited.enabled ||
        basketSizeLimitConfiguration?.warning.enabled,
    }
  );

  if (licensesStatus === "error" || orientationBasketSizeData.status === "error") {
    return { status: "error", data: undefined };
  }

  if (licensesStatus === "loading" || orientationBasketSizeData.status === "loading") {
    return { status: "loading" };
  }

  // Worker substage can be set when worker is in ONBOARDING, ENROLLED, or RESTRICTED stage
  // But we should only show the Limited stage banner if the worker is in ONBOARDING stage, or
  // the worker is in a substage which happens when stage is ENROLLED and substage is set
  const isWorkerInOnboardingOrSubstage =
    worker.stage === WorkerStage.ONBOARDING ||
    (worker.stage === WorkerStage.ENROLLED && worker.substage);

  if (!(isWorkerInOnboardingOrSubstage && basketSizeLimitConfiguration.limited.enabled)) {
    return { status: "success", data: undefined };
  }

  if (worker.substage?.name) {
    const { targeted, nonTargeted } = classifyUserQualifications({
      stage: worker.stage,
      substageName: worker.substage.name,
      licenses,
      basketSizeLimitConfiguration,
    });

    if (targeted.length > 0) {
      return {
        status: "success",
        data: {
          name: worker.substage?.name,
          accessMetadata: worker.substage.accessMetadata,
          qualifications: targeted,
          nonTargetedQualifications: nonTargeted,
        },
      };
    }
  }

  // If substage is not defined as user is in onboarding stage, use access metadata from feature flag
  if (worker.stage === WorkerStage.ONBOARDING && orientationBasketSizeData.status === "success") {
    const { targeted, nonTargeted } = classifyUserQualifications({
      stage: worker.stage,
      substageName: "LIMITED",
      licenses,
      basketSizeLimitConfiguration,
    });

    if (targeted.length > 0) {
      const { scoreThreshold, basketSize, leadTimeThresholdInHrs } =
        basketSizeLimitConfiguration.limited;
      const accessMetadata = {
        scoreThreshold,
        basketSize: orientationBasketSizeData.data?.basketSize ?? basketSize,
        leadTimeThresholdInHrs:
          orientationBasketSizeData.data?.leadTimeInHours ?? leadTimeThresholdInHrs,
      };
      return {
        status: "success",
        data: {
          name: "LIMITED",
          accessMetadata,
          qualifications: targeted,
          nonTargetedQualifications: nonTargeted,
        },
      };
    }
  }

  return { status: "success", data: undefined };
}
